/**
 * Branding parsing — mirror of `sdk/src/browser/branding.ts`.
 *
 * **Must stay in sync with** the SDK side. Adding a new field
 * requires updating both files in the same PR. The contract is
 * the URL parameter naming (`b_logo`, `b_color`, etc.); the type
 * shape is internal to each side.
 *
 * Re-validates every value here — the SDK side already validates
 * but the auth page is the security boundary, and a partner with
 * a custom-built popup-opener could bypass the SDK validation.
 */

export interface BrandingConfig {
  logoUrl?: string;
  brandName?: string;
  primaryColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
  termsUrl?: string;
  privacyUrl?: string;
  showPoweredByHorus?: boolean;
}

const HEX_RE = /^#[0-9a-fA-F]{3,8}$/;

export function readBrandingFromUrl(): BrandingConfig {
  const p = new URLSearchParams(window.location.search);
  const out: BrandingConfig = {};

  const logo = p.get('b_logo');
  if (logo && /^https:\/\//.test(logo)) out.logoUrl = logo;

  const name = p.get('b_name');
  if (name && name.length <= 60) out.brandName = name;

  const color = p.get('b_color');
  if (color && HEX_RE.test(color)) out.primaryColor = color;

  const bg = p.get('b_bg');
  if (bg && HEX_RE.test(bg)) out.backgroundColor = bg;

  const font = p.get('b_font');
  if (font && font.length <= 200) out.fontFamily = font;

  const terms = p.get('b_terms');
  if (terms && /^https:\/\//.test(terms)) out.termsUrl = terms;

  const privacy = p.get('b_privacy');
  if (privacy && /^https:\/\//.test(privacy)) out.privacyUrl = privacy;

  const horus = p.get('b_poweredby');
  if (horus === '0') out.showPoweredByHorus = false;

  return out;
}

export interface AppPolicy {
  whiteLabel: boolean;
  /** Origins registered for this app. Gates the refresh token, nothing else. */
  allowedOrigins: string[];
}

/** No app, no trust — and the footer stays on. */
const CONSERVATIVE_POLICY: AppPolicy = { whiteLabel: false, allowedOrigins: [] };

/**
 * Fetch the per-app policy from the Horus backend: whether this app may
 * hide the "Powered by Horus" footer, and which origins it has registered.
 * The backend `/apps/{appId}/policy` endpoint is unauthenticated, public
 * and cacheable; an unknown appId returns the conservative default.
 *
 * Failures degrade to that default in both dimensions — if the backend is
 * down or the network is flaky we keep the Horus footer rather than
 * accidentally white-labelling an app, and we withhold the refresh token
 * rather than hand a permanent credential to an origin we could not
 * verify. A partner whose policy fetch fails gets the one-hour session
 * that has always been the behaviour, not a broken sign-in.
 */
export async function fetchAppPolicy(appId: string, apiBaseUrl: string): Promise<AppPolicy> {
  if (!appId) return CONSERVATIVE_POLICY;
  try {
    const url = `${stripTrailingSlash(apiBaseUrl)}/apps/${encodeURIComponent(appId)}/policy`;
    const res = await fetch(url, { method: 'GET', credentials: 'omit' });
    if (!res.ok) return CONSERVATIVE_POLICY;
    const body = (await res.json()) as { whiteLabel?: unknown; allowedOrigins?: unknown };
    return {
      whiteLabel: body.whiteLabel === true,
      allowedOrigins: Array.isArray(body.allowedOrigins)
        ? body.allowedOrigins.filter((o): o is string => typeof o === 'string' && o !== '*')
        : [],
    };
  } catch {
    return CONSERVATIVE_POLICY;
  }
}

/**
 * Enforce the white-label tier server-side. Returns a copy of the
 * branding config with `showPoweredByHorus` set to true when the app
 * isn't on the white-label tier — so the SDK can't unilaterally hide
 * the footer just by passing `b_poweredby=0`. The decision belongs
 * to the admin who flipped `whiteLabel: true` on the App record.
 */
export function enforceWhiteLabelTier(
  b: BrandingConfig,
  policy: { whiteLabel: boolean }
): BrandingConfig {
  if (policy.whiteLabel) return b;
  return { ...b, showPoweredByHorus: true };
}

function stripTrailingSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}

/**
 * Apply branding to the page chrome — CSS variables for colors +
 * font, plus the document title prefix. Must be called before any
 * flow renders so all subsequent `el(...)` calls inherit the styled
 * variables.
 */
export function applyBranding(b: BrandingConfig): void {
  const root = document.documentElement;
  if (b.primaryColor) {
    root.style.setProperty('--primary', b.primaryColor);
  }
  if (b.backgroundColor) {
    root.style.setProperty('--bg', b.backgroundColor);
  }
  if (b.fontFamily) {
    root.style.setProperty('--font-family', b.fontFamily);
    document.body.style.fontFamily = b.fontFamily;
  }
  if (b.brandName) {
    document.title = `Sign in · ${b.brandName}`;
  }
}

/**
 * Render the brand header (logo + name) at the top of the auth card.
 * Uses createElement only — query-string-supplied brandName is set
 * via textContent so a partner injecting markup via the URL gets
 * inert text, not parsed HTML.
 */
export function brandHeader(b: BrandingConfig): Node | null {
  const hasLogo = !!b.logoUrl;
  const hasName = !!b.brandName;
  if (!hasLogo && !hasName) return null;

  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '10px';
  wrap.style.marginBottom = '16px';

  if (hasLogo) {
    const img = document.createElement('img');
    img.src = b.logoUrl as string;
    img.alt = b.brandName ?? 'Logo';
    img.style.maxHeight = '36px';
    img.style.maxWidth = '120px';
    img.style.objectFit = 'contain';
    img.referrerPolicy = 'no-referrer';
    wrap.appendChild(img);
  }
  if (hasName) {
    const span = document.createElement('span');
    span.textContent = b.brandName as string;
    span.style.fontSize = '15px';
    span.style.fontWeight = '600';
    span.style.color = 'var(--muted)';
    wrap.appendChild(span);
  }
  return wrap;
}

/**
 * Render the footer — terms / privacy links + optional Horus credit.
 * Falls back gracefully when none of those are configured.
 */
export function brandFooter(b: BrandingConfig): Node {
  const footer = document.createElement('p');
  footer.className = 'footer';
  footer.style.fontSize = '12px';
  footer.style.color = 'var(--muted)';
  footer.style.marginTop = '16px';
  footer.style.textAlign = 'center';

  const parts: Node[] = [];

  if (b.termsUrl) {
    const a = document.createElement('a');
    a.href = b.termsUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = 'Terms';
    parts.push(a);
  }
  if (b.privacyUrl) {
    const a = document.createElement('a');
    a.href = b.privacyUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = 'Privacy';
    parts.push(a);
  }
  // Default `showPoweredByHorus` is true — only false hides it.
  if (b.showPoweredByHorus !== false) {
    parts.push(document.createTextNode('Powered by Horus'));
  }

  parts.forEach((node, i) => {
    if (i > 0) footer.appendChild(document.createTextNode(' · '));
    footer.appendChild(node);
  });

  return footer;
}
